import { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle, X, AlertCircle, ArrowUpRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { z } from "zod";
import { socialLinks, ICONES_SOCIALES } from "@/lib/social-config";

// API Backend
const API_URL = import.meta.env.DEV
  ? // En developpement, on passe par le proxy Vite : le backend n'autorise en
    // CORS que localhost:8080, localhost:5173 et la production, donc un appel
    // direct depuis n'importe quel autre port est bloque par le navigateur.
    "/api"
  : "https://profile-sparkle-magic-production.up.railway.app/api";
const ENDPOINT = `${API_URL}/contact/`;

// Logging sécurisé (seulement en développement)
const secureLog = (message: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.log(message, data);
  }
};

// Schema de validation avec zod (amélioré pour la sécurité)
const contactSchema = z.object({
  name: z.string()
    .trim()
    .nonempty({ message: "Le nom est requis" })
    .max(100, { message: "Le nom doit faire moins de 100 caractères" })
    .refine((val) => !/[<>{}()[\]\\/&%$#@!*+|=~`^]/.test(val), {
      message: "Le nom contient des caractères non autorisés"
    }),
  email: z.string()
    .trim()
    .email({ message: "Email invalide" })
    .max(255, { message: "L'email doit faire moins de 255 caractères" }),
  message: z.string()
    .trim()
    .nonempty({ message: "Le message est requis" })
    .max(1000, { message: "Le message doit faire moins de 1000 caractères" })
});

// Configuration du rate limiting
const RATE_LIMIT_COOLDOWN = 30000; // 30 secondes

export const Contact = ({ variant = "default" }: { variant?: "default" | "portfolio" }) => {
  const isPortfolio = variant === "portfolio";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    _gotcha: "" // Honeypot anti-spam
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Protection anti-spam : si le champ honeypot est rempli, ignorer
    if (formData._gotcha.trim()) {
      secureLog("Spam détecté, soumission ignorée");
      return;
    }

    // Rate limiting - vérifier le cooldown
    const currentTime = Date.now();
    const timeSinceLastSubmission = currentTime - lastSubmissionTime;
    
    if (timeSinceLastSubmission < RATE_LIMIT_COOLDOWN) {
      const remainingTime = Math.ceil((RATE_LIMIT_COOLDOWN - timeSinceLastSubmission) / 1000);
      setIsRateLimited(true);
      toast({
        title: "Trop rapide !",
        description: `Veuillez attendre ${remainingTime} secondes avant de renvoyer un message.`,
        variant: "destructive"
      });
      setTimeout(() => setIsRateLimited(false), remainingTime * 1000);
      return;
    }

    // Validation côté client
    try {
      contactSchema.parse({
        name: formData.name,
        email: formData.email,
        message: formData.message
      });
      setErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((issue) => {
          if (issue.path[0]) {
            newErrors[issue.path[0] as string] = issue.message;
          }
        });
        setErrors(newErrors);
        return;
      }
    }

    setIsSubmitting(true);

    const data = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
    };

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      });

      secureLog("Contact API status:", response.status);

      const payload = await response.json().catch(() => null);

      if (response.ok) {
        setLastSubmissionTime(currentTime);
        setFormData({ name: "", email: "", message: "", _gotcha: "" });
        setShowSuccessModal(true);
        toast({
          title: "Message envoyé !",
          description: "Merci pour votre message. Je vous réponds rapidement.",
        });
      } else if (response.status === 429) {
        const msg = payload?.error || "Trop de messages envoyés. Réessayez plus tard.";
        setErrorMessage(msg);
        setShowErrorModal(true);
        toast({ title: "Limite atteinte", description: msg, variant: "destructive" });
      } else {
        const msg = payload?.error
          || (payload?.errors && Object.values(payload.errors).flat().join(", "))
          || "Échec de l'envoi. Réessayez.";
        setErrorMessage(msg);
        setShowErrorModal(true);
        toast({ title: "Erreur", description: msg, variant: "destructive" });
      }

    } catch (error) {
      console.error("Erreur réseau:", error);
      const msg = "Erreur réseau. Vérifiez votre connexion.";
      setErrorMessage(msg);
      setShowErrorModal(true);
      toast({ title: "Erreur réseau", description: msg, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Effacer l'erreur du champ quand l'utilisateur tape
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };

  // Plus de <section> ni de titre ici : ce bloc vit maintenant DANS la section
  // Contact ouverte par AppelPhone. Il y avait deux titres de niveau 2 qui
  // annoncaient la meme chose, trois paragraphes qui disaient « ecrivez-moi »,
  // et l'ancre #contact atterrissait sur le second bloc, donc le menu sautait
  // par-dessus le visuel de la main.
  const reseaux = (
    <div className="mt-8 flex justify-center gap-3 lg:justify-start">
      {Object.entries(socialLinks).map(([key, social]) => {
        const IconComponent = ICONES_SOCIALES[social.icon as keyof typeof ICONES_SOCIALES];
        return (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors duration-200 ${!social.available ? "opacity-50 cursor-not-allowed" : ""}`}
                asChild={social.available}
                disabled={!social.available}
              >
                {social.available ? (
                  <a href={social.url} target="_blank" rel="noopener noreferrer">
                    <IconComponent className="h-4 w-4" />
                  </a>
                ) : (
                  <span>
                    <IconComponent className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{social.label}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );

  return (
    <div id="formulaire" className={`scroll-mt-24${isPortfolio ? " pf-contact-form" : ""}`}>
      <div className="mx-auto w-full max-w-2xl lg:mx-0">
        {/* Un h3, pas un h2 : la section a deja son titre plus haut. Sans cette
            ligne le formulaire tombe du ciel apres un long visuel. */}
        <h3 data-reveal className="mb-6 text-xl font-semibold text-white lg:text-left">
          Ou écrivez-moi directement
        </h3>
            <Card
            data-reveal
            className={`border-border/50 bg-gradient-card transition-all duration-200 hover:shadow-elegant${isPortfolio ? " pf-form-card" : ""}`}
          >
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" aria-label="Formulaire de contact" aria-busy={isSubmitting}>
                {/* Champ honeypot caché pour anti-spam */}
                <input
                  type="text"
                  name="_gotcha"
                  value={formData._gotcha}
                  onChange={handleChange}
                  style={{ display: "none" }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "name-error" : undefined}
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      className={`transition-all duration-300 focus:shadow-soft ${
                        errors.name ? "border-red-500 focus:border-red-500" : ""
                      }`}
                    />
                    {errors.name && (
                      <p id="name-error" role="alert" className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      maxLength={255}
                      className={`transition-all duration-300 focus:shadow-soft ${
                        errors.email ? "border-red-500 focus:border-red-500" : ""
                      }`}
                    />
                    {errors.email && (
                      <p id="email-error" role="alert" className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? "message-error message-count" : "message-count"}
                      placeholder="Bonjour Tokam, j'aimerais discuter d'un projet..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={1000}
                      rows={5}
                      className={`transition-all duration-300 focus:shadow-soft resize-none ${
                        errors.message ? "border-red-500 focus:border-red-500" : ""
                      }`}
                    />
                    {errors.message && (
                      <p id="message-error" role="alert" className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.message}
                      </p>
                    )}
                    <p id="message-count" className="text-xs text-muted-foreground mt-1">
                      {formData.message.length}/1000 caractères
                    </p>
                  </div>
                </div>

                <p className="text-xs leading-relaxed text-muted-foreground" id="contact-data-notice">
                  Votre nom, votre courriel et votre message servent à traiter votre demande. Le serveur conserve aussi votre adresse IP pour limiter les envois abusifs. N'envoyez pas d'informations sensibles. Pour une question sur vos données : <a className="underline focus-visible:ring-2 active:opacity-70" href="mailto:tokamdarius@gmail.com">tokamdarius@gmail.com</a>.
                </p>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full group bg-gradient-primary hover:shadow-glow transition-all duration-300"
                  disabled={isSubmitting || isRateLimited}
                  aria-busy={isSubmitting}
                  aria-describedby="contact-data-notice"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer le message
                      <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {!isPortfolio && reseaux}

        {/* Modal de succès */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className={`max-w-md${isPortfolio ? " pf-form-dialog" : ""}`}>
            <DialogHeader>
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <DialogTitle className="text-center">Message envoyé !</DialogTitle>
              <DialogDescription className="text-center">
                Merci pour votre message. Je vous réponds dans les plus brefs délais.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center pt-4">
              <Button onClick={() => setShowSuccessModal(false)} className="px-8">
                Parfait !
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal d'erreur */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className={`max-w-md${isPortfolio ? " pf-form-dialog" : ""}`}>
            <DialogHeader>
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <DialogTitle className="text-center">Erreur d'envoi</DialogTitle>
              <DialogDescription className="text-center">
                {errorMessage}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowErrorModal(false)}>
                Fermer
              </Button>
              <Button onClick={() => {
                setShowErrorModal(false);
                // Refocaliser sur le formulaire pour un nouvel essai
                document.getElementById("name")?.focus();
              }}>
                Réessayer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
